import { getLendersByMerchantId, getMerchantById } from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { StructureNavigator } from '@/components/modules/MerchantDetail/MerchantStructure/components/StructureNavigator';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { ModuleCorePermissionProps } from '@/config/types';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';
import { useCache } from '@/hooks/requests/useCache';
import { useSetPageLoadingWhenRequesting } from '@/hooks/requests/useSetPageLoadingWhenRequesting';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface MerchantStructureProps extends ModuleCorePermissionProps {
  hideChannelList?: boolean;
}

const MerchantStructure = ({ canEdit = false, hideChannelList = false }: MerchantStructureProps) => {
  const { merchantId } = useParams();

  if (!merchantId) return null;

  const { data: lenders, loading: lendersLoading } = useCache(
    getLendersByMerchantId,
    { merchantId },
    { fallbackData: [] },
  );

  const { data: merchant, loading: merchantLoading, revalidate } = useCache(getMerchantById, { merchantId });
  const { merchantStructure, loading: merchantStructureLoading } = useGetMerchantStructureById(merchantId);

  const firstMerchantStructureLoad = merchantStructureLoading && !merchantStructure;
  const isLoading = merchantLoading || firstMerchantStructureLoad || lendersLoading;

  useSetPageLoadingWhenRequesting('MERCHANT_STRUCTURE', [isLoading]);

  const showNavigator =
    (canEdit ? lenders?.length : true) &&
    merchant?.approvalStatus.isApprovedByCommercial &&
    merchant?.approvalStatus.isApprovedByRisk;

  const actions: ActionsOption[] | undefined =
    showNavigator && canEdit
      ? [
          {
            display: <Text tId={'REFRESH_DATA'} />,
            onClick: () => {
              revalidate();
            },
            iconName: 'refresh',
          },
        ]
      : undefined;

  return (
    <SubSectionWrapper title={hideChannelList ? 'AGENTS' : 'CHANNELS_AND_AGENTS'} specificActionsOptionsArray={actions}>
      {showNavigator && merchantStructure ? (
        <StructureNavigator
          merchantStructure={merchantStructure}
          editModeAllowed={canEdit}
          hideChannelList={hideChannelList}
          loading={merchantStructureLoading}
        />
      ) : (
        <PendingDataWarning />
      )}
    </SubSectionWrapper>
  );
};

export const MerchantStructureModule = withPermissionsModule(MerchantStructure);
